import React, { useEffect, useMemo, useRef, useState } from 'react';
import { surfaceRadius } from 'settings/brandRadius';
import { Box, Collapse, Stack, Typography } from '@mui/material';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * VERA-315 — the certificate page's STATE CARDS, ranked and stacked.
 *
 * ## What this replaces
 *
 * The page grew three independent "something is going on with this certificate"
 * surfaces, each deciding its own position and its own weight:
 *
 *  | state | where it rendered | what it was |
 *  |---|---|---|
 *  | lost / stolen | below the title | `MissingStateCallout`, a state card |
 *  | pending gift / transfer | below the title | `TransferSummarySection`, a state card |
 *  | suspicious activity (an invalid scan) | ABOVE the title | a bespoke MUI `Alert` |
 *
 * So the one that was NOT a card was also the one that outranked the product's own
 * name by sitting above it — a hierarchy nobody chose, arrived at because it was
 * added last and there was room at the top. Worse, with two states live the page
 * showed two full blocks and let vertical order (an accident of source order) stand
 * in for severity.
 *
 * This makes rank explicit and caps the cost of N states at ONE card plus ONE row.
 *
 * ## Why a stack with progressive disclosure, and NOT a carousel
 *
 * A swipe/peek carousel was rejected for two reasons, in this order:
 *
 *  1. `ImageCarousel` sits DIRECTLY ABOVE this block and already owns the
 *     horizontal swipe. A second horizontal gesture 100px below the first, on a
 *     375px phone (working-conventions §37 — the phone IS the product here), is a
 *     gesture collision the user resolves by accident.
 *  2. Peeking hides urgency behind a gesture. The entire point of ranking is that
 *     the most severe state is unmissable; a carousel makes states 2..N equally
 *     invisible to a reader who does not swipe, which is the opposite.
 *
 * ## The rules this component guarantees
 *
 *  - The top-ranked state renders as the full card, byte-identical to what it
 *    rendered standalone.
 *  - Everything else collapses into ONE summary row, so the block's baseline height
 *    is `card + row` for N = 2, 3 or 10.
 *  - With a SINGLE state there is no row at all — the output degrades to exactly
 *    the previous UI, which is the no-regression case that matters most because it
 *    is by far the most common one.
 *
 * ## ⚠️ DOM ORDER IS FIXED; only CSS `order` moves
 *
 * This is the load-bearing implementation detail and it is not an optimisation.
 *
 * `TransferSummarySection` OWNS IN-FLIGHT MUTATION UI — the scan-tag drawer and the
 * `GenericSuccessDrawer` that says "Gift canceled" both live inside it (see
 * `GiftOutActionsSection`). Those drawers are mounted whether or not the card is
 * showing, and the success drawer is opened by the very mutation that then makes
 * the card retire. If reordering or hiding the card unmounted that subtree, the
 * success drawer would be torn down at the exact moment it was meant to appear.
 *
 * So two invariants:
 *
 *  - Children are rendered in a STABLE, KEYED source order and repositioned only
 *    with flex `order`. React therefore never moves an instance between parents.
 *  - Every item is wrapped in the SAME `Collapse` — including the top card, whose
 *    `in` is simply always true. A card that was collapsed and is promoted to the
 *    top must not change wrapper shape, because that is a remount too.
 *  - `Collapse` is used WITHOUT `unmountOnExit`. A hidden card stays mounted at
 *    `height: 0`. Its drawers are unaffected: MUI `Drawer`/`Dialog` portal to
 *    `document.body`, so a collapsed ancestor cannot hide them.
 */

/**
 * Rank, high to low. Gaps of 10 so a state can be slotted between two existing
 * ones without renumbering.
 *
 * ## The ordering is corroborated by code, not just asserted
 *
 * `packages/ui/utils/scanStatus.ts` already argues the missing-vs-suspicious half
 * of this, at length, for the map's pin colours: an invalid scan is ranked "a notch
 * BELOW Stolen and Tampered rather than identical to them", because
 *
 *   > Those two are confirmed states — an owner has declared the item stolen, or
 *   > the tag itself reported a physical breach. An invalid scan is EVIDENCE:
 *   > possibly a counterfeit, possibly a cloned link, possibly nothing. Ranking
 *   > evidence equal to a confirmed state overstates it.
 *
 * That is exactly the distinction wanted here, so this reuses its conclusion rather
 * than inventing a second, possibly disagreeing, severity axis. A pending transfer
 * is below both because nothing is wrong: it is a routine action awaiting a second
 * party, and its own card's copy is an instruction, not a warning.
 */
export const STATE_SEVERITY = {
  /** Reported lost or stolen. A confirmed state, declared by the owner. */
  MISSING: 30,
  /** An invalid scan was recorded — evidence of a possible counterfeit. */
  SUSPICIOUS: 20,
  /** A gift or transfer is in flight, waiting on the other party. */
  PENDING_TRANSFER: 10,
  /**
   * The owner has never proven physical possession — an outstanding TO-DO, not a
   * problem (VERA-304).
   *
   * Lowest rank, and the gap to `PENDING_TRANSFER` is deliberate: every state above
   * it reports something that HAPPENED to the certificate (it was reported missing,
   * an invalid scan was recorded, a transfer is in flight and waiting on someone
   * else). This one reports something the owner has simply not got round to. It is
   * worth surfacing — nothing else on the page prompts for it — but it must never
   * outrank news.
   *
   * In practice it cannot collide with `PENDING_TRANSFER` anyway: verifying requires
   * `status === 'active'`, and a certificate mid-transfer is not active. The rank is
   * stated rather than assumed because that coupling is not this file's to rely on.
   */
  VERIFY_POSSESSION: 5,
} as const;

/**
 * At or above this rank, a state is never allowed to hide behind the summary row.
 *
 * ⚠️ HONESTY: with today's three states this is UNREACHABLE, and saying so is more
 * useful than implying the guard fires. `MISSING` is the highest rank, so a missing
 * state is always the TOP card and therefore never one of the collapsed remainder —
 * and a certificate cannot be lost AND stolen at once (`status` is one value), so
 * two missing states cannot coexist either.
 *
 * It is here anyway because the failure it prevents is silent and the check is one
 * line: the day a fourth state lands at the confirmed-state tier (a confirmed
 * counterfeit, a tamper breach), it auto-expands BY CONSTRUCTION rather than
 * because someone reading this file remembered to think about it. Do not delete it
 * as dead code without re-reading that sentence.
 */
const AUTO_EXPAND_SEVERITY = STATE_SEVERITY.MISSING;

/**
 * `order` for a card that is not currently one of the ranked states — the dormant
 * `TransferSummarySection` being the only real case. It renders nothing, so the
 * value only has to be larger than any real rank and than the toggle's own.
 */
const DORMANT_ORDER = 900;

/** `order` for the summary row when expanded: below every card. */
const EXPANDED_TOGGLE_ORDER = 800;

export interface StateCardItem {
  /** Stable across renders — this is the React key that keeps instances alive. */
  key: string;
  /** One of `STATE_SEVERITY`. */
  severity: number;
  /**
   * Glyph for the summary row. It is the only thing that says WHICH states are
   * hidden, so it should name the state (a gift box for a gift, an alert octagon
   * for lost/stolen) rather than be a generic dot.
   */
  icon: React.ReactNode;
  /** Short name, used for the row's accessible label. Never rendered as text. */
  label: string;
  /**
   * Does this state currently apply?
   *
   * An item may be present-but-inactive: that is how a card which owns mounted
   * drawers stays mounted while contributing nothing. Inactive items take no part
   * in ranking, in the "N more" count, or in the single-state degradation test.
   */
  active: boolean;
  /**
   * The full card. A NODE, not a render function, because the always-mounted case
   * above requires it to be evaluated whether or not it is shown.
   */
  card: React.ReactNode;
}

export const StateCardStack = ({ items }: { items: StateCardItem[] }) => {
  const ranked = useMemo(
    () =>
      items
        .filter(item => item.active)
        // Descending, and `sort` is stable in every engine this ships to (ES2019),
        // so two states at the same rank keep the caller's declaration order rather
        // than swapping around between renders.
        .sort((a, b) => b.severity - a.severity),
    [items],
  );

  const rankByKey = useMemo(() => {
    const map = new Map<string, number>();
    ranked.forEach((item, index) => map.set(item.key, index));
    return map;
  }, [ranked]);

  const hidden = ranked.slice(1);
  const hasSummaryRow = hidden.length > 0;

  /**
   * A high-severity state in the REMAINDER forces the stack open. See
   * `AUTO_EXPAND_SEVERITY` for why this currently cannot fire.
   */
  const autoExpand = hidden.some(item => item.severity >= AUTO_EXPAND_SEVERITY);

  const [expanded, setExpanded] = useState(autoExpand);
  /*
   * A FLOOR, not a lock. The initial value alone would miss the real case: scan
   * logs and the certificate arrive on separate requests, so a state can become
   * high-severity several hundred ms after this mounts. Once open, the user may
   * still collapse it — auto-expand exists so urgency is not hidden by DEFAULT,
   * not to take the control away.
   */
  useEffect(() => {
    if (autoExpand) setExpanded(true);
  }, [autoExpand]);

  /**
   * The `order` each key last held while it was ranked.
   *
   * Without this a card that stops applying — "Report found" on a stolen item —
   * would jump to `DORMANT_ORDER` (the bottom) INSTANTLY and only then animate its
   * height away, because `order` is not animatable and the two changes are not
   * simultaneous. Remembering the last rank keeps it collapsing where it stands,
   * which is the in-place exit VERA-301 built the `Collapse` for in the first place.
   *
   * Written during render on purpose: it is derived from `rankByKey`, idempotent,
   * and never read before it is written for that key in the same pass.
   *
   * Known tie: the outgoing card holds rank 0 while its replacement is promoted to
   * rank 0, so for the length of the animation two items share an `order`. CSS
   * breaks the tie on DOM order, which is the fixed source order — stable, if
   * arbitrary. That is strictly better than the jump it replaces.
   */
  const lastOrder = useRef<Record<string, number>>({});
  const orderFor = (item: StateCardItem) => {
    const rank = rankByKey.get(item.key);
    if (rank !== undefined) {
      lastOrder.current[item.key] = rank;
      return rank;
    }
    return lastOrder.current[item.key] ?? DORMANT_ORDER;
  };

  const isShown = (item: StateCardItem) => {
    const rank = rankByKey.get(item.key);
    if (rank === undefined) return false;
    return rank === 0 || expanded;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {items.map(item => (
        <Box key={item.key} sx={{ order: orderFor(item) }}>
          {/* Uniform wrapper for every item — see the DOM-ORDER note at the top of
              this file. `in` is true for the top card, so it renders entered on
              first paint with no entrance animation (`appear` defaults to false),
              which matters because `TransferSummarySection` owns its own carefully
              conditional entrance and must not be given a second one. */}
          <Collapse in={isShown(item)}>{item.card}</Collapse>
        </Box>
      ))}

      {hasSummaryRow && (
        <Box sx={{ order: expanded ? EXPANDED_TOGGLE_ORDER : 1 }}>
          <SummaryRow
            hidden={hidden}
            expanded={expanded}
            onToggle={() => setExpanded(value => !value)}
          />
        </Box>
      )}
    </Box>
  );
};

/**
 * The one compact row that stands in for states 2..N.
 *
 * It takes the state card's EDGE (1px `#E4E4E7`, 12px radius) but not its LIFT.
 * `stateCardSurface` records what the shadow means on those cards — "a pending
 * transfer or a missing report sits ON the certificate, it is not part of it" — and
 * this row is not another such report. It is a control belonging to the card above
 * it, so it borrows the family's edge to read as attached and skips the shadow so it
 * does not read as a fourth card.
 */
const SummaryRow = ({
  hidden,
  expanded,
  onToggle,
}: {
  hidden: StateCardItem[];
  expanded: boolean;
  onToggle: () => void;
}) => {
  const label = expanded
    ? 'Show less'
    : `${hidden.length} more to review`;

  return (
    <Stack
      direction='row'
      alignItems='center'
      spacing={1}
      role='button'
      tabIndex={0}
      aria-expanded={expanded}
      aria-label={
        expanded
          ? 'Show less'
          : `${hidden.length} more to review: ${hidden.map(item => item.label).join(', ')}`
      }
      onClick={onToggle}
      onKeyDown={event => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onToggle();
        }
      }}
      sx={{
        /*
          -8px against the card's own 16px bottom margin, so the gap reads as 8 and
          the row sits UNDER the card rather than floating between it and whatever
          comes next.

          Corrected here rather than by lowering `stateCardSurfaceSx.mb`: that value
          is shared with every state card in every other context (including the
          single-card case this component must leave untouched), and changing it for
          one consumer is how the two cards drifted apart the first time.

          ⚠️ apps/app runs MUI's DEFAULT 8px spacing base, NOT @repo/ui's 4px
          (working-conventions §29). `-1` is -8px here and `2` is 16px. Copying a
          factor across from a platform file doubles it.
        */
        mt: -1,
        mb: 2,
        px: 1.5,
        py: 1,
        cursor: 'pointer',
        // VRG-562: the same step `stateCardSurfaceSx` takes, so the collapsed row
        // and the cards it collapses cannot drift apart.
        borderRadius: surfaceRadius,
        backgroundColor: 'background.paper',
        border: '1px solid #E4E4E7',
        // The state cards' own borders render at this colour whatever they author —
        // `index.css`'s `#vera-shadow-root *` sets `border-color` with ID
        // specificity. Writing the resolved value keeps this row honest about what
        // it will actually paint. See the ⚠️ in `stateCardSurface`.
        '&:focus-visible': {
          outline: theme => `2px solid ${theme.palette.text.primary}`,
          outlineOffset: '2px',
        },
      }}
    >
      {/* Icons only while collapsed. Expanded, every state they preview is on
          screen in full, so they would be a legend for a list you are already
          reading. */}
      {!expanded && (
        <Stack direction='row' alignItems='center' spacing={0.75} sx={{ flexShrink: 0 }}>
          {hidden.map(item => (
            <Box
              key={item.key}
              aria-hidden
              sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}
            >
              {item.icon}
            </Box>
          ))}
        </Stack>
      )}
      <Typography variant='body2' sx={{ flexGrow: 1, color: 'text.secondary' }}>
        {label}
      </Typography>
      <Box aria-hidden sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </Box>
    </Stack>
  );
};
